import { Box, Typography, Alert, useTheme, alpha } from "@mui/material";

export const num = (v) => (v === null || v === undefined ? "—" : v);

/** Traffic-light tone for a value against good/warn thresholds. */
export const tone = (theme, value, good, warn, higherWorse = false) => {
  if (value === null || value === undefined) return theme.palette.text.secondary;
  const green = "#2e7d32", amber = "#ed6c02", red = "#d32f2f";
  if (higherWorse) return value <= good ? green : value <= warn ? amber : red;
  return value >= good ? green : value >= warn ? amber : red;
};

const StatCard = ({ label, value, unit, color }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        flex: "1 1 160px",
        minWidth: 150,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.12 : 0.9),
      }}
    >
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, letterSpacing: "0.04em" }}>
        {label.toUpperCase()}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color: color || theme.palette.text.primary, mt: 0.5 }}>
        {num(value)}
        {value !== null && value !== undefined && unit ? (
          <Typography component="span" variant="body2" sx={{ color: theme.palette.text.secondary, ml: 0.5 }}>
            {unit}
          </Typography>
        ) : null}
      </Typography>
    </Box>
  );
};

const Panel = ({ title, rows }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        flex: "1 1 240px",
        minWidth: 220,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.08 : 0.6),
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      {rows.map(([k, v]) => (
        <Box key={k} sx={{ display: "flex", justifyContent: "space-between", py: 0.4 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {k}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
            {v}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/** Renders a /simulate result: alerts, hero strip, and the detail panels.
 * `showHero={false}` hides the stat-card strip (e.g. when the caller renders its
 * own gauge dials) but keeps the alerts and detail panels. */
const SimResult = ({ result, premiumLocked, showHero = true }) => {
  const theme = useTheme();
  if (!result) return null;

  return (
    <Box sx={{ mt: 3 }}>
      {premiumLocked && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This combination has verified thrust-stand data. Showing the modeled estimate —
          upgrade for verified results.
        </Alert>
      )}
      {result.accuracy_note && (
        <Alert severity={result.source === "measured" ? "success" : "info"} sx={{ mb: 2 }}>
          {result.accuracy_note}
        </Alert>
      )}
      {result.warnings?.map((w, i) => (
        <Alert key={i} severity="warning" sx={{ mb: 1 }}>
          {w}
        </Alert>
      ))}

      {/* Hero strip */}
      {showHero && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1, mb: 3 }}>
          <StatCard label="Thrust : Weight" value={result.system.thrust_to_weight} unit=": 1"
            color={tone(theme, result.system.thrust_to_weight, 2, 1.3)} />
          <StatCard label="Hover flight time" value={result.system.hover_flight_time_min} unit="min" />
          <StatCard label="Hover throttle" value={result.system.hover_throttle_pct} unit="%"
            color={tone(theme, result.system.hover_throttle_pct, 60, 80, true)} />
          <StatCard label="Max thrust" value={result.system.max_total_thrust_g} unit="g" />
        </Box>
      )}

      {/* Detail panels */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        {result.hover && (
          <Panel
            title="Motor @ Hover"
            rows={[
              ["Throttle", `${result.hover.throttle_pct} %`],
              ["Revolutions", `${result.hover.rpm} rpm`],
              ["Current", `${result.hover.current_a} A`],
              ["Voltage", `${result.hover.voltage_v} V`],
              ["Thrust / motor", `${result.hover.thrust_g} g`],
              ["Efficiency", `${num(result.hover.motor_efficiency_pct)} %`],
              ["el. Power", `${result.hover.electric_power_w} W`],
            ]}
          />
        )}
        <Panel
          title="Motor @ Max"
          rows={[
            ["Throttle", `${result.max.throttle_pct} %`],
            ["Revolutions", `${result.max.rpm} rpm`],
            ["Current", `${result.max.current_a} A`],
            ["Voltage", `${result.max.voltage_v} V`],
            ["Thrust / motor", `${result.max.thrust_g} g`],
            ["Efficiency", `${num(result.max.motor_efficiency_pct)} %`],
            ["el. Power", `${result.max.electric_power_w} W`],
          ]}
        />
        <Panel
          title="Battery"
          rows={[
            ["Pack", `${result.battery.cells_series}S · ${result.battery.pack_voltage_v} V`],
            ["Capacity", `${num(result.battery.capacity_mah)} mAh`],
            ["Usable", `${num(result.battery.usable_mah)} mAh`],
            ["Energy", `${result.battery.energy_wh} Wh`],
            ["Resistance", `${result.battery.resistance_ohm} Ω`],
          ]}
        />
        <Panel
          title="System"
          rows={[
            ["All-up weight", `${result.system.all_up_weight_g} g`],
            ["Motors", `${result.system.num_motors}${result.system.coaxial ? " (coaxial)" : ""}`],
            ["Add. payload", `${num(result.system.add_payload_g)} g`],
            ["Hover current", `${num(result.system.hover_current_a)} A`],
            ["Specific thrust", `${num(result.system.specific_thrust_hover_g_per_w)} g/W`],
            ["Air density", result.air_density_kg_m3 == null ? "— (measured)" : `${result.air_density_kg_m3} kg/m³`],
            ...(result.system.max_tilt_deg != null ? [["Max tilt", `${result.system.max_tilt_deg}°`]] : []),
            ...(result.system.disc_area_dm2 != null ? [["Total disc area", `${result.system.disc_area_dm2} dm²`]] : []),
            ...(result.system.est_temp_hover_c != null
              ? [["est. Temp (hover/max)", `${result.system.est_temp_hover_c} / ${num(result.system.est_temp_max_c)} °C`]]
              : []),
          ]}
        />
      </Box>
    </Box>
  );
};

export default SimResult;
